export const defaultWorldMapColors = [
	"#112224", // Darkest (High intensity ~80-100%)
	"#1e4f56", // Deep Teal
	"#326b72", // Teal
	"#4e868d", // Medium Teal
	"#6fa1a7", // Light-Medium Teal
	"#92babf", // Light Teal
	"#a7c4c7", // Lightest (Visible talent ~1-5%)
];

/**
 * Filters country labels to prevent overlap on the map.
 * Uses a greedy approach: accepts labels one by one and skips any that
 * would overlap with an already-accepted label in geographic coordinate space.
 */
export const filterOverlappingLabels = <T extends {lng: number; lat: number; name: string}>(
	labels: T[],
	zoom: number,
	fontSize: number,
): T[] => {
	// Approximate character width in geographic degrees at the given zoom.
	// At zoom 1, 1 degree ≈ a small amount of screen space. The constants
	// were tuned visually so that labels in Europe stop overlapping.
	const charWidthDeg = (fontSize * 0.55) / zoom; // longitude degrees per character
	const lineHeightDeg = (fontSize * 1.4) / zoom; // latitude degrees per label

	type Rect = {x1: number; y1: number; x2: number; y2: number};

	const placed: Rect[] = [];

	const getRect = (label: T): Rect => {
		const halfWidth = (label.name.length * charWidthDeg) / 2;
		const halfHeight = lineHeightDeg / 2;

		return {
			x1: label.lng - halfWidth,
			y1: label.lat - halfHeight,
			x2: label.lng + halfWidth,
			y2: label.lat + halfHeight,
		};
	};

	const overlaps = (a: Rect, b: Rect): boolean => {
		return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
	};

	return labels.filter((label) => {
		const rect = getRect(label);
		const hasCollision = placed.some((p) => overlaps(rect, p));

		if (!hasCollision) {
			placed.push(rect);

			return true;
		}

		return false;
	});
};

export const defaultGeographyStyle = (marked: boolean, color: string) => {
	return {
		default: {
			fill: marked ? color : "#DBE4E6",
			strokeWidth: 0.75,
			outline: "none",
			transition: "all 250ms",
			cursor: marked ? "pointer" : "default",
		},
		hover: {
			fill: "rgba(91, 159, 201, 0.7)",
			strokeWidth: 0.75,
			outline: "none",
			transition: "all 250ms",
			cursor: "pointer",
		},
		pressed: {
			outline: "none",
			transition: "all 250ms",
			cursor: "grabbing",
		},
	};
};
